import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import API from "../../services/API";
import baseElementTemplate from "../../data/baseElement.json";
import { AppButton } from "../ui";
import BulkUploadElementDialog from "./BulkUploadElementDialog";
import { useContent } from "../../context/ContentContext";

const BulkElementUploader = ({ onNewElementCreated }) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const { trackElementBuild } = useContent();

  const handleOpenDialog = () => {
    setUploadDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setUploadDialogOpen(false);
  };

  const cloneBaseElementTemplate = () => JSON.parse(JSON.stringify(baseElementTemplate));

  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = (err) => reject(err);
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const createElementPayload = async ({ file, namePrefix }) => {
    const newElementPayload = cloneBaseElementTemplate();

    const elementId = uuidv4();
    newElementPayload.id = newElementPayload.dataObjectID = elementId;
    newElementPayload.labelData.displayName = `${namePrefix}${file.name}`;
    newElementPayload.labelData.description = file.name;
    newElementPayload.fileName = file.name;

    const fileExtension = (file.name.split(".").pop() || "").toLowerCase();

    switch (fileExtension) {
      case "png":
      case "jpg":
      case "jpeg":
        newElementPayload.visualizerData.dataType = 1;
        newElementPayload.visualizerData.visualizationType = 3;
        newElementPayload.visualizerData.defaultMaterialName = "DefaultSpriteMaterial";
        newElementPayload.worldUnit.value = 40;

        try {
          const { width, height } = await getImageDimensions(file);
          if (width > 0) {
            newElementPayload.visualizerData.xValue = height / width;
          }
        } catch (err) {
          console.error("Failed to get image dimensions:", err);
        }
        break;
      case "obj":
      case "fbx":
      case "glb":
      case "gltf":
        newElementPayload.visualizerData.dataType = 4;
        newElementPayload.visualizerData.visualizationType = 2;
        break;
      case "swc":
        newElementPayload.visualizerData.dataType = 4;
        newElementPayload.visualizerData.visualizationType = 2;
        newElementPayload.worldUnit.value = 1;
        newElementPayload.worldUnit.units = 4;
        break;
      case "pdb":
        newElementPayload.visualizerData.dataType = 1;
        newElementPayload.visualizerData.visualizationType = 6;
        newElementPayload.worldUnit.value = 1;
        newElementPayload.worldUnit.units = 2;
        break;
      default:
        break;
    }

    return newElementPayload;
  };

  const uploadSingleElement = async ({ file, namePrefix }) => {
    const newElementPayload = await createElementPayload({ file, namePrefix });

    const response = await API.post("elements/", newElementPayload, {
      requiresAuth: true,
    });

    const { sourceUploadUrl } = response.data.data;
    if (file && sourceUploadUrl) {
      const uploadHeaders = file.type ? { "Content-Type": file.type } : undefined;
      await fetch(sourceUploadUrl, {
        method: "PUT",
        body: file,
        headers: uploadHeaders,
      });
    }

    await API.post("build-element", newElementPayload, {
      requiresAuth: true,
    });

    return {
      id: newElementPayload.id,
      name: newElementPayload.labelData.displayName,
      description: newElementPayload.labelData.description,
      status: {
        type: "BUILDING",
        timestamp: new Date().toISOString(),
      },
    };
  };

  const handleBulkUpload = async ({ namePrefix = "", files = [] }) => {
    if (!Array.isArray(files) || files.length === 0) {
      return;
    }

    try {
      for (const file of files) {
        const createdElement = await uploadSingleElement({ file, namePrefix });
        trackElementBuild(createdElement.id, createdElement);
        if (onNewElementCreated) {
          onNewElementCreated(createdElement);
        }
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Error uploading bulk elements:", error);
      throw error;
    }
  };

  return (
    <>
      <AppButton kind="secondary" onClick={handleOpenDialog}>
        Bulk Upload Elements
      </AppButton>
      <BulkUploadElementDialog
        open={uploadDialogOpen}
        onClose={handleCloseDialog}
        onUpload={handleBulkUpload}
      />
    </>
  );
};

export default BulkElementUploader;
